import type { RampConfig } from '@gorampe/calc-engine';
import { optimizeTreadSections, computePlatforms } from '@gorampe/calc-engine';
import type {
  SceneLayout,
  PlatformPlacement,
  TreadPlacement,
  RailPlacement,
  LandingPlacement,
  PattePlacement,
  CornerPostPlacement,
  TransitionPlacement,
} from '../types';
import { SCALE_IN_TO_UNIT } from '../types';
import type { HardwareGroup, HardwarePlacement } from '../hardware/instances';
import {
  DEFAULT_HEAD_PLATFORM_DEPTH_IN,
  DEFAULT_HEAD_PLATFORM_WIDTH_IN,
  EDGE_OVERLAP_IN,
  LANDING_SIZE_IN,
} from './constants';
import { selectPatteSku, selectTransitionSku, selectCornerPostSku } from '../bom/skuMapping';

export function layoutStraight(config: RampConfig, run: number): SceneLayout {
  const platforms: PlatformPlacement[] = [];
  const treads: TreadPlacement[] = [];
  const rails: RailPlacement[] = [];
  const landings: LandingPlacement[] = [];
  const pattes: PattePlacement[] = [];
  const rivetPlacements: HardwarePlacement[] = [];
  const PATTE_INTERVAL_IN = 60; // patte every 5 ft

  // PDF officiel GoRampe: head platform 60×60 (default) or 60×96.
  // Width fixed at 60" per official spec (no 36/44 variation for the head platform).
  const headDepthIn = config.headPlatformDepthIn ?? DEFAULT_HEAD_PLATFORM_DEPTH_IN;
  const headWidthIn = DEFAULT_HEAD_PLATFORM_WIDTH_IN;

  const pitchAngle = Math.atan2(config.heightIn, run);

  // NOTE (passe-seuil business rule): For heights ≤ 6" (typical doorway threshold),
  // a passe-seuil (F07xxx catalogue) may be more appropriate than a head platform.
  // Currently the configurator always emits a head platform (F01005 48×48) for all heights.
  // Future feature: detect low-height ramps (heightIn ≤ 6") and offer a passe-seuil
  // alternative. For heights > 6" (e.g. h=56"), a full head platform is always required.

  // Head platform: negative Z, slight overlap with first tread for visual connection
  // PDF officiel: platform is 60×60 (5×5) or 60×96 (5×8), independent of arm width.
  // Even with 36" arm, the platform is 60" wide per PDF spec (overhang accepted).
  platforms.push({
    id: 'head',
    kind: 'head',
    position: {
      x: 0,
      y: config.heightIn * SCALE_IN_TO_UNIT,
      z: (-headDepthIn / 2 + EDGE_OVERLAP_IN) * SCALE_IN_TO_UNIT,
    },
    widthIn: headWidthIn,
    depthIn: headDepthIn,
  });

  // Treads: cumulate from z=0 toward +run, with optimised section lengths
  const sections = optimizeTreadSections(run);
  let cursorIn = 0;
  for (const sec of sections) {
    for (let i = 0; i < sec.qty; i++) {
      const midRunIn = cursorIn + sec.lengthIn / 2;
      const yIn = (1 - midRunIn / run) * config.heightIn;
      treads.push({
        id: `tread-${sec.lengthIn}-${i}-${cursorIn}`,
        position: {
          x: 0,
          y: yIn * SCALE_IN_TO_UNIT,
          z: midRunIn * SCALE_IN_TO_UNIT,
        },
        rotationY: 0,
        pitchAngle,
        lengthIn: sec.lengthIn,
        widthIn: config.widthIn,
      });

      // Rivets along both side edges, one every RIVET_PITCH_IN inches
      const RIVET_PITCH_IN = 8;
      const halfW = config.widthIn / 2 - 2;
      const treadStartZ = cursorIn;
      const rivetCount = Math.floor(sec.lengthIn / RIVET_PITCH_IN) + 1;
      for (let r = 0; r < rivetCount; r++) {
        const zOff = r * RIVET_PITCH_IN;
        for (const xOff of [-halfW, halfW]) {
          rivetPlacements.push({
            position: { x: xOff, y: yIn, z: treadStartZ + zOff },
            rotation: { x: 0, y: 0, z: 0 },
            parentRefId: `tread-${sec.lengthIn}-${i}-${cursorIn}`,
          });
        }
      }

      cursorIn += sec.lengthIn;
    }
  }

  // Rails on both sides — positioned at mid-height of slope, pitched to match
  if (config.railType !== 'aucun') {
    const railHeightIn = config.usage === 'com' ? 42 : 36;
    const xOffset = (config.widthIn / 2) * SCALE_IN_TO_UNIT;
    for (const side of ['left', 'right'] as const) {
      rails.push({
        id: `rail-${side}`,
        position: {
          x: side === 'left' ? -xOffset : xOffset,
          y: (config.heightIn / 2) * SCALE_IN_TO_UNIT,
          z: (run / 2) * SCALE_IN_TO_UNIT,
        },
        rotationY: 0,
        pitchAngle,
        lengthIn: run,
        heightIn: railHeightIn,
        type: config.railType,
        side,
      });
    }

    // Head platform side rails ONLY (left + right). NO back rail — that side
    // connects to building/doorway, must remain OPEN for user entry.
    const headTopY = config.heightIn * SCALE_IN_TO_UNIT;
    const headMidZ = (-headDepthIn / 2 + EDGE_OVERLAP_IN) * SCALE_IN_TO_UNIT;
    // Left side rail — aligned to ARM width (continuous with arm-left rail at z=0)
    rails.push({
      id: 'rail-head-left',
      position: { x: -xOffset, y: headTopY, z: headMidZ },
      rotationY: 0,
      pitchAngle: 0,
      lengthIn: headDepthIn,
      heightIn: railHeightIn,
      type: config.railType,
      side: 'left',
    });
    // Right side rail — aligned to ARM width (continuous with arm-right rail at z=0)
    rails.push({
      id: 'rail-head-right',
      position: { x: xOffset, y: headTopY, z: headMidZ },
      rotationY: 0,
      pitchAngle: 0,
      lengthIn: headDepthIn,
      heightIn: railHeightIn,
      type: config.railType,
      side: 'right',
    });
  }

  // Rest landings injection — distribute evenly along the run
  const platformsMeta = computePlatforms(config);
  const restMetas = platformsMeta.filter((p) => p.kind === 'rest-landing');
  restMetas.forEach((_, idx) => {
    const fraction = (idx + 1) / (restMetas.length + 1);
    const cumRunIn = run * fraction;
    const yIn = (1 - fraction) * config.heightIn;
    landings.push({
      id: `landing-${idx}`,
      position: {
        x: 0,
        y: yIn * SCALE_IN_TO_UNIT,
        z: cumRunIn * SCALE_IN_TO_UNIT,
      },
      widthIn: LANDING_SIZE_IN,
      depthIn: LANDING_SIZE_IN,
    });
  });

  // Pattes — every PATTE_INTERVAL_IN inches along the run
  const patteCount = Math.max(1, Math.floor(run / PATTE_INTERVAL_IN));
  for (let i = 1; i <= patteCount; i++) {
    const fraction = i / (patteCount + 1);
    const zIn = run * fraction;
    const yIn = (1 - fraction) * config.heightIn;
    if (yIn < 1) continue; // skip if at ground level
    pattes.push({
      id: `patte-${i}`,
      position: {
        x: 0,
        y: yIn * SCALE_IN_TO_UNIT,
        z: zIn * SCALE_IN_TO_UNIT,
      },
      rotationY: 0,
      widthIn: config.widthIn,
      heightIn: yIn,
      partNumber: selectPatteSku(yIn),
    });
  }

  const hardwareGroups: HardwareGroup[] = [
    {
      partNumber: 'RIVET-3-16-DOME',
      geometry: 'rivet-dome-3-16',
      placements: rivetPlacements,
    },
  ];

  // Corner posts at 4 corners of head platform — tie head rails to arm rails
  const cornerPosts: CornerPostPlacement[] = [];
  if (config.railType !== 'aucun') {
    const railHeightIn = config.usage === 'com' ? 42 : 36;
    const xOff = config.widthIn / 2;
    // Posts at head platform's south corners (where arm rails start)
    const cpSku = selectCornerPostSku(railHeightIn);
    cornerPosts.push({
      id: 'cp-head-sw',
      position: {
        x: -xOff * SCALE_IN_TO_UNIT,
        y: config.heightIn * SCALE_IN_TO_UNIT,
        z: EDGE_OVERLAP_IN * SCALE_IN_TO_UNIT,
      },
      heightIn: railHeightIn,
      partNumber: cpSku,
    });
    cornerPosts.push({
      id: 'cp-head-se',
      position: {
        x: xOff * SCALE_IN_TO_UNIT,
        y: config.heightIn * SCALE_IN_TO_UNIT,
        z: EDGE_OVERLAP_IN * SCALE_IN_TO_UNIT,
      },
      heightIn: railHeightIn,
      partNumber: cpSku,
    });
    // Posts at bottom of arm 1 (ground level, where rails end)
    cornerPosts.push({
      id: 'cp-arm1-sw',
      position: {
        x: -xOff * SCALE_IN_TO_UNIT,
        y: 0,
        z: run * SCALE_IN_TO_UNIT,
      },
      heightIn: railHeightIn,
      partNumber: cpSku,
    });
    cornerPosts.push({
      id: 'cp-arm1-se',
      position: {
        x: xOff * SCALE_IN_TO_UNIT,
        y: 0,
        z: run * SCALE_IN_TO_UNIT,
      },
      heightIn: railHeightIn,
      partNumber: cpSku,
    });
  }

  // Transition plate at ramp base (ground level)
  const transitions: TransitionPlacement[] = [
    {
      id: 'trans-base',
      position: { x: 0, y: 0, z: (run + 5) * SCALE_IN_TO_UNIT },
      rotationY: 0,
      widthIn: config.widthIn,
      partNumber: selectTransitionSku(config.widthIn, config.floor),
    },
  ];

  return {
    platforms,
    treads,
    rails,
    landings,
    pattes,
    cornerPosts,
    transitions,
    hardwareGroups,
    floor: config.floor,
  };
}
